import { CreateAuthHeaders, FetchGet } from '../utils/dataModel';

/**
 * Service class for handling realtime dashboard data operations.
 */
class DashboardRealtimeService {
  /**
   * Gets realtime data for the last 3 days with progressive registration numbers.
   * @returns {Object} Preprocessed realtime data with hourly entries grouped by day
   */
  getRealtimeData(selectedLicense) {
    return new Promise((resolve, reject) => {
      const apiUrl = `/api/v1/dashboard/realtime?license_id=${selectedLicense.license_id}`;
      FetchGet(apiUrl, CreateAuthHeaders("session_id"))
        .then(data => {
          resolve(this.processRealtimeData(data));
        })
        .catch(error => {
          console.error('Error fetching realtime data:', error);
          reject(error);
        });
    });
  }

  /**
   * Aggregates data by hour for a specific date
   * @param {Array} hourData - Array of hourly data
   * @param {string} metric - Metric to aggregate ('register_num' or 'usage_total')
   * @returns {Object} Hourly aggregated data
   */
  aggregateByHour(hourData, metric) {
    const hourlyData = {};
    hourData.forEach(item => {
      hourlyData[item.hour] = item[metric];
    });
    return hourlyData;
  }

  /**
   * Aggregates data by country for a specific day's data
   * @param {Array} hourData - Array of hourly data
   * @param {string} metric - Metric to aggregate ('register_num' or 'usage_total')
   * @returns {Object} Country aggregated data
   */
  aggregateByCountry(hourData, metric) {
    const countryData = {};
    hourData
      .filter(item => item.country)
      .forEach(item => {
        countryData[item.country] = (countryData[item.country] || 0) + item[metric];
      });
    return countryData;
  }

  /**
   * Processes the realtime data to structure it for display
   * @param {Array} data - Array of realtime data
   * @returns {Object} Processed data
   */
  processRealtimeData(data) {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const dayBeforeYesterday = new Date(today);
    dayBeforeYesterday.setDate(today.getDate() - 2);

    const dates = [
      today.toISOString().split('T')[0],
      yesterday.toISOString().split('T')[0],
      dayBeforeYesterday.toISOString().split('T')[0]
    ];

    // Initialize the structured data
    const structuredData = {
      today: { date: dates[0], hourData: [] },
      yesterday: { date: dates[1], hourData: [] },
      dayBeforeYesterday: { date: dates[2], hourData: [] }
    };

    // Process data for each day
    const processDay = (date) => {
      const hourData = [];
      for (let hour = 0; hour < 24; hour++) {
        const hourStr = hour.toString().padStart(2, '0');
        const timestamp = `${date} ${hourStr}:00:00`;
        
        // Find existing data for this hour
        const existingData = data.filter(item => item.timestamp === timestamp);
        if (existingData && existingData.length > 0) {
          existingData.forEach(item => {
            hourData.push({
              hour: hour,
              timestamp,
              num_licenses_new_used: item.num_licenses_new_used,
              usage_total: item.usage_total,
              country: item.country
            });
          })
        }
      }

      return hourData;
    };

    // Process each day
    structuredData.today.hourData = processDay(dates[0]);
    structuredData.yesterday.hourData = processDay(dates[1]);
    structuredData.dayBeforeYesterday.hourData = processDay(dates[2]);

    return structuredData;
  }
}

const realtime_service = new DashboardRealtimeService();
export default realtime_service;